import { Empty } from '@bufbuild/protobuf';
import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { Button, WidgetCard } from '@/shared/ui';

import { authModel } from '@/entities/auth';
import { organizationModel } from '@/entities/organization';

import { useLogout } from '@/features/logout';
import { DisableMFAModal, MFAEnrollmentModal } from '@/features/mfa';

import { MFA_SETUP_DATE_FORMAT } from '../config';

import styles from './SecuritySettings.module.scss';

type SecuritySettingsProps = {
  className?: string;
};

/**
 * The account's second factor: state, the way to turn it on or replace the
 * authenticator, and the way to turn it off.
 */
export const SecuritySettings: FC<SecuritySettingsProps> = (props) => {
  const { className } = props;

  const { formatDate, formatMessage } = useIntl();
  const { isPhone } = useMedia();
  const dispatch = useAppDispatch();
  const logout = useLogout();

  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [isDisableOpen, setIsDisableOpen] = useState(false);

  const mfaStatus = useAppSelector(authModel.selectors.selectMFAStatus);
  const mfaStatusLoading = useAppSelector(
    authModel.selectors.selectMFAStatusLoading,
  );
  const isMFASettingsEnabled = useAppSelector(
    organizationModel.selectors.selectIsMFASettingsEnabled,
  );
  const isRequiredByOrganization =
    useAppSelector(organizationModel.selectors.selectCurrentOrganization)
      ?.MFARequiredForActor ?? false;

  const isEnabled = mfaStatus?.Enabled ?? false;

  // Turning it off is either refused by the backend or undone by the
  // enrolment gate on the next read.
  const isMandatory = mfaStatus?.Enforced || isRequiredByOrganization;

  if (!isMFASettingsEnabled) {
    return null;
  }

  // Without the status "off" is indistinguishable from "unknown", and the
  // off-branch would offer to rotate a working credential.
  if (mfaStatusLoading === 'failed' && !isSetupOpen) {
    return (
      <WidgetCard
        className={cn(styles.container, className)}
        data-component="security-settings"
      >
        <h4 className={isPhone ? 'h5' : 'h4'}>
          <FormattedMessage id="settings.security" defaultMessage="Security" />
        </h4>

        <p className={cn(styles.hint, 'p3t')}>
          <FormattedMessage
            id="mfa.settings.loadFailed"
            defaultMessage="Two-step verification status could not be loaded."
          />
        </p>

        <Button
          className={styles.action}
          variant="tertiary"
          size="medium"
          onClick={() => dispatch(authModel.thunks.mfaStatus(new Empty()))}
        >
          <FormattedMessage id="global.retry" defaultMessage="Try again" />
        </Button>
      </WidgetCard>
    );
  }

  return (
    <>
      <WidgetCard
        className={cn(styles.container, className)}
        loading={mfaStatusLoading === 'pending' && !mfaStatus}
        data-component="security-settings"
      >
        <h4 className={isPhone ? 'h5' : 'h4'}>
          <FormattedMessage id="settings.security" defaultMessage="Security" />
        </h4>

        <div className={styles.rows}>
          <div className={cn(styles.row, styles.stateRow)}>
            <p className={cn(styles.label, 'p2')}>
              <FormattedMessage
                id="mfa.settings.twoStepVerification"
                defaultMessage="Two-step verification"
              />
            </p>

            {isEnabled ? (
              <div className={styles.state}>
                <span className={styles.stateDot} />

                <p className="p2">
                  <FormattedMessage
                    id="mfa.settings.on"
                    defaultMessage="On — authenticator app"
                  />
                </p>
              </div>
            ) : (
              <p className={cn(styles.state, 'p2')}>
                <FormattedMessage id="mfa.settings.off" defaultMessage="Off" />
              </p>
            )}

            {isEnabled ? (
              !isMandatory && (
                <Button
                  className={styles.action}
                  variant="tertiary"
                  size="medium"
                  danger
                  onClick={() => setIsDisableOpen(true)}
                >
                  <FormattedMessage
                    id="global.disable"
                    defaultMessage="Disable"
                  />
                </Button>
              )
            ) : (
              <Button
                className={styles.action}
                variant="tertiary"
                size="medium"
                onClick={() => setIsSetupOpen(true)}
              >
                <FormattedMessage
                  id="mfa.settings.setUp"
                  defaultMessage="Set up"
                />
              </Button>
            )}
          </div>

          {isEnabled && mfaStatus?.SetupAt && (
            <div className={styles.row}>
              <p className={cn(styles.label, 'p2')}>
                <FormattedMessage
                  id="mfa.settings.connected"
                  defaultMessage="Connected"
                />
              </p>

              <p className="p2">
                {formatDate(mfaStatus.SetupAt.toDate(), MFA_SETUP_DATE_FORMAT)}
              </p>
            </div>
          )}

          {!isEnabled && (
            <div className={styles.row}>
              <p className={cn(styles.label, 'p2')}>
                <FormattedMessage
                  id="mfa.settings.signInMethod"
                  defaultMessage="Sign-in method"
                />
              </p>

              <p className="p2">
                <FormattedMessage
                  id="mfa.settings.emailAndPassword"
                  defaultMessage="Email and password"
                />
              </p>
            </div>
          )}
        </div>

        <p className={cn(styles.hint, 'p3t')}>
          {isEnabled ? (
            <FormattedMessage
              id="mfa.settings.replaceHint"
              defaultMessage="Moving to another device? <button>Set up a new authenticator</button> using a code from your current app. If you lost access, use a recovery code when signing in."
              values={{
                button: (chunks) => (
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => setIsSetupOpen(true)}
                  >
                    {chunks}
                  </button>
                ),
              }}
            />
          ) : (
            <FormattedMessage
              id="mfa.settings.offHint"
              defaultMessage="A 6-digit code from an authenticator app is requested at every sign in. Codes work without a network connection."
            />
          )}
        </p>

        {isEnabled && isMandatory && (
          <p className={cn(styles.hint, 'p3t')}>
            <FormattedMessage
              id="mfa.settings.mandatoryHint"
              defaultMessage="Two-step verification is required for your account and cannot be turned off."
            />
          </p>
        )}
      </WidgetCard>

      {/* Outside the card: it swaps its content for a skeleton while the
          status is re-read, which would unmount an open enrolment mid-flow. */}
      {isSetupOpen && (
        <MFAEnrollmentModal
          collapsibleSetupKey
          action={isEnabled ? 'replace' : 'enroll'}
          onSignInRequired={logout}
          onCompleted={() => setIsSetupOpen(false)}
          onCancel={() => setIsSetupOpen(false)}
          title={formatMessage({
            id: 'mfa.setup.title',
            defaultMessage: 'Set up two-step verification',
          })}
          cancelLabel={
            <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
          }
          description={
            <p className="p2">
              <FormattedMessage
                id="mfa.setup.registrationDescription"
                defaultMessage="Scan the QR code with Google Authenticator or another authenticator app"
              />
            </p>
          }
        />
      )}

      <DisableMFAModal
        isOpen={isDisableOpen}
        onCancel={() => setIsDisableOpen(false)}
        onDisabled={() => setIsDisableOpen(false)}
      />
    </>
  );
};
